import React, { useContext, useEffect, useState } from 'react';
import TenantContext from '@/contexts/TenantContext';
import { motion } from 'framer-motion';
import { BarChart3, Clock, DollarSign, Printer, Download, Sparkles, TrendingUp, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantReportsPage = () => {
    const { tenantSlug, tenant } = useContext(TenantContext);
    const [metrics, setMetrics] = useState({ messages: 0, customers: 0, sessions: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                // Here we fetch the real tenant to get their actual metrics
                const res = await axios.get(`${API_URL}/api/tenants/${tenantSlug}`);
                if (res.data && res.data.metrics) {
                   setMetrics(res.data.metrics);
                } else if (res.data && res.data.stats) {
                   // Fallback for Master Dashboard stats format
                   setMetrics({ 
                       messages: res.data.stats.messages_processed || 0,
                       customers: res.data.stats.unique_users || 0,
                       sessions: res.data.stats.active_sessions || 0 
                   });
                }
            } catch (error) {
                console.error("Error fetching metrics", error);
            } finally {
                setLoading(false);
            }
        };
        if (tenantSlug) fetchMetrics();
    }, [tenantSlug]);

    const handlePrint = () => {
        window.print();
    };

    // ROI Math
    // Asunción: IA ahorra 2 minutos por mensaje. Valor de hora de trabajo = $5 USD (approx $100 MXN).
    const minutesSaved = metrics.messages * 2;
    const hoursSaved = (minutesSaved / 60).toFixed(1);
    const moneySaved = (hoursSaved * 5).toFixed(2); // Dolares aprox

  return (
    <div className="space-y-8 pb-32 print:bg-white print:text-black">
      {/* Header (Hidden in print to save ink, or restyled) */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 print:hidden">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-violet-400" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-violet-400">Auditoría de Impacto</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
            Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-500">Reportes</span>
          </h1>
          <p className="text-slate-400 mt-2 font-medium italic text-sm">Descubre cuánto tiempo y dinero te ahorró Fluxo este mes.</p>
        </motion.div>
        
        <div className="flex gap-4">
            <button onClick={handlePrint} className="px-6 py-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] font-black uppercase text-white transition-all flex items-center gap-2 group shadow-xl">
                <Printer className="w-4 h-4 group-hover:scale-110 transition-transform" /> Imprimir / PDF
            </button>
        </div>
      </div>

      {/* REPORTE PARA IMPRIMIR */}
      <div className="mt-8 print:mt-0 max-w-4xl mx-auto">
        <div className="bg-slate-900/40 print:bg-white border border-white/10 print:border-slate-300 rounded-[3rem] print:rounded-none overflow-hidden backdrop-blur-xl shadow-2xl print:shadow-none">
            {/* Cabecera del Documento */}
            <div className="p-10 border-b border-white/5 print:border-slate-200 bg-gradient-to-b from-white/5 to-transparent print:from-slate-50 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-12 opacity-10 print:opacity-5">
                    <Sparkles className="w-40 h-40" />
                </div>
                <div className="relative z-10 flex justify-between items-start">
                    <div>
                        <h2 className="text-3xl font-black text-white print:text-slate-900 tracking-tight uppercase">Resumen Ejecutivo</h2>
                        <p className="text-violet-400 font-bold mt-1 uppercase tracking-widest text-xs">{tenant?.name || 'Cliente SaaS'}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Fecha de Emisión</p>
                        <p className="text-white print:text-slate-800 font-medium">{new Date().toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}</p>
                    </div>
                </div>
            </div>

            {/* Metrica Estrella: EL ROI */}
            <div className="p-10 text-center relative overflow-hidden">
                 <div className="absolute inset-0 bg-violet-500/5 blur-3xl rounded-full" />
                 <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }} 
                    animate={{ scale: 1, opacity: 1 }}
                    className="relative z-10"
                 >
                     <p className="text-xs font-black uppercase tracking-[0.3em] text-slate-400 print:text-slate-500 mb-6">Tiempo Humano Ahorrado</p>
                     <div className="text-7xl md:text-8xl font-black text-white print:text-slate-900 tracking-tighter flex items-center justify-center gap-4">
                         {hoursSaved} <span className="text-2xl text-violet-400 uppercase tracking-widest relative -top-6">Horas</span>
                     </div>
                     <p className="text-sm font-medium text-slate-400 print:text-slate-600 mt-6 max-w-lg mx-auto">
                         Tu Agente IA procesó todas las interacciones de este periodo automáticamente. Si un humano hubiera hecho este trabajo, habrías gastado aproximadamente <strong className="text-emerald-400 print:text-emerald-600">${moneySaved} USD</strong> en costos operativos.
                     </p>
                 </motion.div>
            </div>

            {/* Desglose Operativo */}
            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-white/5 print:border-slate-200 divide-y md:divide-y-0 md:divide-x divide-white/5 print:divide-slate-200">
                <div className="p-8 text-center bg-white/5 print:bg-transparent">
                    <BarChart3 className="w-8 h-8 text-blue-400 mx-auto mb-4" />
                    <h4 className="text-4xl font-black text-white print:text-slate-900">{metrics.messages || 0}</h4>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-2">Mensajes Respondidos</p>
                </div>
                <div className="p-8 text-center bg-white/5 print:bg-transparent">
                    <Users className="w-8 h-8 text-purple-400 mx-auto mb-4" />
                    <h4 className="text-4xl font-black text-white print:text-slate-900">{metrics.customers || 0}</h4>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-2">Clientes Atendidos</p>
                </div>
                <div className="p-8 text-center bg-white/5 print:bg-transparent">
                    <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
                    <h4 className="text-4xl font-black text-white print:text-slate-900">{((metrics.messages * 100) / (metrics.messages || 1)).toFixed(1)}%</h4>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-2">Tasa de Efectividad IA</p>
                </div>
            </div>

            {/* Footer */}
            <div className="p-6 bg-black/40 print:bg-slate-100 text-center text-[9px] uppercase tracking-widest font-bold text-slate-600 border-t border-white/5 print:border-slate-200">
                Reporte Generado Automáticamente por Fluxo Co. • Inteligencia Artificial Aplicada a Negocios
            </div>
        </div>
      </div>

    </div>
  );
};

// SVG Icon Helpers
function Users(props) { return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> }

export default TenantReportsPage;
